import React, { Component } from 'react';
import PDF from 'react-pdf-js';
import { withTranslation, WithTranslation } from 'react-i18next';

import { FileType } from '../../types/file';
import Loader from './Loader';
import Button from './Button';

type Props = {
  loadDoc: () => Promise<FileType>,
  itemId: number,
  match: Object,
  onDownload: () => void,
  onExit: () => void
} & WithTranslation

type State = {
  file: FileType,
  blob: Blob,
  loading: boolean,
  error?: string,
  page: Number,
  pages: Number,
  scale: Number
}

// key codes
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const MAX_SCALE = 5;
const MIN_SCALE = 1;

class FileViewer extends Component<Props, State> {
  state: State = {
    file: null,
    blob: null,
    pdfSrc: null,
    imgSrc: null,
    loading: false,
    error: null,
    page: 1,
    pages: 1,
    scale: 1.0
  };

  props: Props

  componentWillMount() {
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
    this.getFile(this.props);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.itemId !== this.props.itemId || nextProps.match.params.id !== this.props.match.params.id) {
      this.getFile(nextProps);
    }
  }

  getFile = (props) => {
    const itemId = props.itemId || props.match.params.id;
    this.setState({
      loading: true
    });
    props.getFileInfo(itemId)
      .then((file) => {
        props.getBlob(itemId)
          .then((blob) => {
            this.handleDocumentFormat(file, blob).then((subState) => {
              this.setState({
                loading: false,
                file,
                blob,
                ...subState
              });
            });
          })
          .catch((error) => this.setState({ error }));
      })
      .catch((error) => this.setState({ error }));
  }
  componentWillUnmount = () => {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
  }
  handleDocumentFormat = (file: FileType, blob: Blob): Promise<> => {
    return new Promise((resolve, reject) => {
      if (file.mime_type.indexOf('pdf') > -1) {
        resolve({ pdfSrc: window.URL.createObjectURL(blob) });
      } else if (file.mime_type.indexOf('image') > -1) {
        resolve({ imgSrc: window.URL.createObjectURL(blob) });
      } else if (file.mime_type.indexOf('text') > -1) {
        const fileReader: FileReader = new FileReader();
        fileReader.onloadend = () => resolve({ text: fileReader.result });
        fileReader.readAsText(blob);
      } else {
        reject();
      }
    });
  }
  onDocumentComplete = (pages: number) => {
    this.setState({ page: 1, pages });
  }

  onPageComplete = (page: number) => {
    this.setState({ page });
  }

  handlePrevious = () => {
    if (this.state.page !== 1) {
      this.setState({ page: this.state.page - 1 });
    }
  }

  handleNext = () => {
    if (this.state.page !== this.state.pages) {
      this.setState({ page: this.state.page + 1 });
    }
  }

  handleKeyDown = (event) => {
    if (event.keyCode === LEFT_ARROW && this.state.page !== 1) {
      this.handlePrevious();
    } else if (event.keyCode === RIGHT_ARROW && this.state.page !== this.state.pages) {
      this.handleNext();
    }
  }

  handleScaleUp = () => {
    if (this.state.scale < MAX_SCALE) {
      this.setState({
        scale: this.state.scale + 0.5
      });
    }
  }

  handleScaleDown = () => {
    if (this.state.scale > MIN_SCALE) {
      this.setState({
        scale: this.state.scale - 0.5
      });
    }
  }

  renderPagination(page: number, pages: number): any {
    const { t } = this.props;
    const iconNames = {
      download: 'ic-download',
      zoomOut: 'ic-zoom-out',
      zoomIn: 'ic-zoom-in',
      back: 'ic-arrow-back',
      next: 'ic-arrow-forward'
    };
    return (
      <nav className='pdf-controls'>
        {this.props.onDownload ? (
          <Button
            title={t('download_PDF')}
            className='is-round is-secondary'
            onClick={this.props.onDownload}
            iconName={iconNames.download}
          />
        ) : ''}
        <div className='scale-control'>
          <Button
            title={t('zoom_out')}
            className='is-round is-secondary'
            onClick={this.handleScaleDown}
            iconName={iconNames.zoomOut}
            isDisabled={this.state.scale <= MIN_SCALE}
          />

          <Button
            title={t('zoom_in')}
            className='is-round is-secondary'
            onClick={this.handleScaleUp}
            iconName={iconNames.zoomIn}
            isDisabled={this.state.scale >= MAX_SCALE}
          />
        </div>

        <div className='pagination-control'>
          <Button
            title={t('previous_page')}
            className='is-round is-secondary'
            onClick={this.handlePrevious}
            iconName={iconNames.back}
            isDisabled={page === 1}
          />
          <Button
            title={t('next_page')}
            className='is-round is-secondary'
            onClick={this.handleNext}
            iconName={iconNames.next}
            isDisabled={page === pages}
          />
        </div>
      </nav>
    );
  }
  render() {
    let pagination = null;
    if (this.state.pages) {
      pagination = this.renderPagination(this.state.page, this.state.pages);
    }
    return (

      <div className='pdf-viewer'>
        <header>
          <div className='title'>
            <h2 className='is-white'>{this.state.file ? this.state.file.file_name : '' }</h2>
          </div>
          <div className='options'>
            <div className='delete is-large' onClick={this.props.onExit} role='button' tabIndex={0} />
          </div>
        </header>
        <div className='spinner is-background' />
        {this.state.error &&
          <div>
            <p className='has-error'>{this.state.error}</p>
          </div>
        }
        <div className='document-viewer-container viewer-container'>
          <Loader loading={this.state.loading} />
          {this.state.error &&
            <div>
              <p className='has-error'>{this.state.error}</p>
            </div>
          }
          {this.state.imgSrc &&
            <div className='is-centered'>
              <img src={this.state.imgSrc} alt={this.state.file ? this.state.file.file_name : ''} />
            </div>
          }
          {this.state.text &&
            <div className='is-centered'>
              <pre>{this.state.text}</pre>
            </div>
          }
          {this.state.pdfSrc &&
            <div className='is-centered'>
              <PDF
                file={this.state.pdfSrc}
                page={this.state.page}
                scale={this.state.scale}
                onDocumentComplete={this.onDocumentComplete}
                onPageComplete={this.onPageComplete}
              />
              {pagination}
            </div>
          }
        </div>
      </div>
    );
  }
}

export default withTranslation('fileViewer')(FileViewer);



// WEBPACK FOOTER //
// ./src/components/shared/FileViewer.js